/**
 * **매장 운영 화면(사업장 관리자의 「매장 관리」) — 진입·대행·잠긴 칸 한 곳.** 의존성 없는 순수 모듈이다
 * (Vue·Element·`@/` 별칭 금지).
 *
 * 관리점·운영사(R-E2)는 새 화면을 갖지 않는다. 사업장 관리자가 매장을 골라 쓰는 **기존 화면**을 그대로 쓰고,
 * 매장은 세션이 아니라 `storeCd`(경로·쿼리·본문)로 든다. 그래서 이 파일이 답하는 것은 셋뿐이다:
 *
 * 1. **들어갈 수 있는가** — 서버 액션이 답한다([canEnterStoreOps]). 역할 이름을 적지 않는다: 관리점·운영사는
 *    SQL 034 가 같은 URL 에 역할을 더한 것만으로 같은 화면을 쓴다.
 * 2. **어디로 들어가는가** — [storeOpsRoute] (`?storeCd=` 가 그 매장의 상세를 곧바로 연다).
 * 3. **무엇을 바꿀 수 없는가** — 서버 응답의 `lockedFields`([isStoreFieldLocked]). 대행 계정에게만 실리고
 *    점주·사업장·내부 응답에는 **키가 없다** — 그래서 「키가 있으면 대행」 하나로 판정한다([isStoreProxyForm]).
 *    역할 이름으로 판정하면 겸직(사업장 관리자 + 관리점) 계정에서 서버와 답이 갈린다.
 *
 * 저장은 서버가 강제한다(잠긴 칸은 보내도 현재 값으로 되돌린다 — 400 이 아니다). 화면의 읽기 전용은 혼란 방지다.
 *
 * 매장 운영 화면 밖의 대행 화면 중 **사이드 메뉴로 닿지 않는 것**은 대행 띠가 바로가기로 연다([storeProxyDiscountRoute]).
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/R-E2-관리점운영대행-appserver-report.md` §5.
 */
// 확장자를 적는 이유: 단위 테스트가 앱 번들 밖(node)에서 이 파일을 그대로 읽는다
import { hasAction } from './authActions.js'
import { DISCOUNT_STORE_SOURCE, discountStoreSource, pickedDiscountRoute } from './discountStoreSource.js'

/** 매장 운영 화면의 라우트 — 사업장 관리자의 「매장 관리」(SQL 034 가 관리점·운영사에 준 사이드 메뉴와 같은 값) */
export const STORE_OPS_PATH = '/v/business/store/list'

/** 세션 액션 id — `sys_action` 과 같은 문자열 */
export const STORE_OPS_ACTION = Object.freeze({
  /** `GET /store` — 매장 목록(대행 계정은 서버가 매핑 매장으로 좁힌다) */
  SEARCH: 'StoreSearch',
  /** `GET /store/*` — 매장 상세 */
  GET: 'StoreGet',
  /** `PUT /store/*` — 매장 상세 저장 */
  UP: 'StoreUp',
})

/** 화면 문구 — 띠 문장은 브리프 §3 그대로 */
export const STORE_OPS_TEXT = Object.freeze({
  /** 「○○ 매장을 대행 중 — …」 의 ○○ 뒤 */
  proxyBannerTail: '매장을 대행 중 — 사업자 정보·정산 계좌·PG·계정·해지는 점주나 오투오에 요청하세요',
  /** 이름을 모를 때(구 응답)의 띠 — 이름 없이도 무엇을 못 하는지는 말한다 */
  proxyBannerNoName: '매장을 대행 중입니다 — 사업자 정보·정산 계좌·PG·계정·해지는 점주나 오투오에 요청하세요',
  /** 관리 매장 목록의 매장명 링크 */
  openStoreOps: '매장 운영 화면으로 이동',
  /** 대행 띠의 바로가기 — 그 매장의 할인 관리 화면 */
  discountShortcut: '이 매장의 할인 관리로 이동',
})

/**
 * 매장 상세를 **열고 저장할 수 있는가** — 매장 관리 다이얼로그가 서는 조건이다.
 *
 * 다이얼로그는 수정 폼이라 읽기(`GET /store/{cd}`)와 저장(`PUT /store/{cd}`)을 함께 가져야 한다. 읽기만 가진
 * 내부 관리자(`admin`·`mng`)는 이 화면에서 다이얼로그 대신 외부 채널 패널만 따로 연다(종전 그대로).
 *
 * @param {?object} auth 세션 스냅샷(`auth/auth` 게터)
 * @returns {boolean}
 */
export function canEditStoreDetail(auth) {
  return hasAction(auth, STORE_OPS_ACTION.GET) && hasAction(auth, STORE_OPS_ACTION.UP)
}

/**
 * 매장 운영 화면으로 **들어갈 수 있는가** — 목록을 읽고(진입하자마자 부른다) 상세를 열고 저장할 수 있어야 한다.
 *
 * 목록 액션 없이 들어가면 첫 조회가 보안 계층 403 이 되고, 이 앱의 그 403 은 세션을 끝낸다 — 그래서 링크를
 * 그리는 자리가 이 판정을 먼저 묻는다.
 *
 * @param {?object} auth 세션 스냅샷(`auth/auth` 게터)
 * @returns {boolean}
 */
export function canEnterStoreOps(auth) {
  return hasAction(auth, STORE_OPS_ACTION.SEARCH) && canEditStoreDetail(auth)
}

/**
 * 그 매장의 운영 화면 — `?storeCd=` 가 들어가자마자 그 매장의 상세를 연다.
 *
 * @param {number|string} storeCd
 * @returns {{path: string, query: {storeCd: string}}}
 */
export function storeOpsRoute(storeCd) {
  return { path: STORE_OPS_PATH, query: { storeCd: String(storeCd) } }
}

/**
 * **대행 계정의 상세인가** — 서버가 `lockedFields` 키를 실었는가(값이 아니라 키의 유무가 신호다).
 *
 * @param {?object} form 매장 상세 폼(`storeDetailForm` 결과 — 응답 칸을 그대로 갖는다)
 * @returns {boolean}
 */
export function isStoreProxyForm(form) {
  return !!form && Array.isArray(form.lockedFields)
}

/**
 * 이 칸은 **저장해도 바뀌지 않는가** — 화면은 읽기 전용으로 그린다.
 *
 * @param {?object} form 매장 상세 폼
 * @param {string} key 응답·요청 JSON 의 칸 이름(서버 `StoreLockedField.key`)
 * @returns {boolean}
 */
export function isStoreFieldLocked(form, key) {
  return isStoreProxyForm(form) && form.lockedFields.indexOf(key) > -1
}

/**
 * 잠겼고 **서버가 값을 내리지 않은** 칸인가 — 그리지 않는다.
 *
 * 대행 계정의 상세에는 PG 키·취소 비밀번호·계좌 번호가 **키째 없다**(`null` 이 아니다). 그 칸을 빈 채로 그리면
 * 「값이 없다」로 읽히므로, 값을 모르는 칸은 자리째 뺀다. 키가 없어도 잠기지 않은 칸(구 응답·등록 폼)은
 * 종전대로 그린다.
 *
 * @param {?object} form 매장 상세 폼
 * @param {string} key
 * @returns {boolean}
 */
export function isStoreFieldWithheld(form, key) {
  return isStoreFieldLocked(form, key) && !Object.prototype.hasOwnProperty.call(form, key)
}

/**
 * 매장 상세 위의 대행 띠 — 대행 계정이 아니면 빈 문자열(띠를 그리지 않는다).
 *
 * @param {?object} form 매장 상세 폼
 * @returns {string}
 */
export function storeProxyBanner(form) {
  if (!isStoreProxyForm(form)) return ''

  const name = form.storeNmLan || form.storeNm || ''

  return name ? `${name} ${STORE_OPS_TEXT.proxyBannerTail}` : STORE_OPS_TEXT.proxyBannerNoName
}

/**
 * 대행 띠의 **「할인 관리」 바로가기** — 그 매장의 할인 화면(고른 매장 원천). 없으면 `null`(링크를 그리지 않는다).
 *
 * 왜 메뉴가 아니라 띠인가: 사이드 메뉴의 「할인 관리」(리소스 198)는 리얼에서 사업장 계정에게 **의도적으로 숨긴**
 * 메뉴다(07-08 샵인샵 SQL 머리말의 네이티브 숨김 — 메뉴 행은 있고 페이지 리소스가 미사용). 그것을 여는 것은 사업장
 * 170여 계정에 메뉴를 되살리는 사업 변경이라 열지 않고(R-H 2차 리뷰 결정), 대행 계정은 매장을 대행하는 그 자리에서
 * 할인 화면으로 간다. 할인 API 는 SQL 034 가 준 것 그대로다.
 *
 * 링크는 **들어가자마자 부르는 목록 액션**이 있을 때만 선다 — 없는 세션의 첫 `GET /discount` 는 보안 계층 403 이고
 * 이 앱의 그 403 은 세션을 끝낸다. 띠와 같은 자리이므로 대행 상세(`lockedFields` 있음)에만 선다.
 *
 * @param {?object} form 매장 상세 폼
 * @param {?object} auth 세션 스냅샷(`auth/auth` 게터)
 * @returns {?{path: string, query: {storeCd: string}}}
 */
export function storeProxyDiscountRoute(form, auth) {
  if (!isStoreProxyForm(form) || form.storeCd == null) return null
  if (!hasAction(auth, discountStoreSource(DISCOUNT_STORE_SOURCE.PICKED).actions.search)) return null

  return pickedDiscountRoute(form.storeCd)
}
